import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Instrument, InstrumentAPI, Section, SectionDescriptionAPI, SectionQuestionHeaderAPI, SectionTitleAPI, HelpAPI, IntroductionAPI, TermsConditionsAPI, Questionnaire } from 'src/models/question.model';
import { environment } from "@env/environment";
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

const API_URL = `${environment.api.baseUrl || ""}${environment.api.services.question}`;

@Injectable({
  providedIn: 'root'
})
export class QuestionService {

  constructor(private httpClient: HttpClient) { }

  getInstrument(instrumentId: string = "", culture: string | undefined) {

    let headers = new HttpHeaders()
    headers = headers.set('content-type', 'application/json')

    // Initialize Params Object
    let params = new HttpParams();
    params = params.append('instrumentsId', instrumentId);

    //let url = "assets/test-instrument.json";
    let url = API_URL + '/instrument';

    return this.httpClient.get<InstrumentAPI>(url, {
      params: params,
      headers: headers
    })
      .pipe(map((res: InstrumentAPI) => {

        // loop through the section array to keep only the selected culture
        const sections: Section[] = [];
        res.section?.forEach((res) => {
          const sec = new Section;
          const secDescription = new SectionDescriptionAPI;
          const secQuestionHeader = new SectionQuestionHeaderAPI;
          const secSectionTitle = new SectionTitleAPI;
          sec.sectionName = res.sectionName;
          sec.sectionDescriptionText = res.sectionDescription?.filter((sn) => { return sn.languageCode?.toLowerCase() == culture?.toLowerCase() }).map((text) => { return text.sectionDescriptionText }).toString();
          sec.sectionQuestionHeaderText = res.sectionQuestionHeader?.filter((sqh) => { return sqh.languageCode?.toLowerCase() == culture?.toLowerCase() }).map((text) => { return text.sectionQuestionHeaderText }).toString();
          sec.sectionTitleText = res.sectionTitle?.filter((st) => { return st.languageCode?.toLowerCase() == culture?.toLowerCase() }).map((text) => { return text.sectionTitleText }).toString();

          const sectionDiscripPatientTermsText = res.sectionDescription?.filter((sd) =>
            sd.languageCode?.toLocaleLowerCase() === culture?.toLocaleLowerCase()).flatMap((sd) =>
              sd.patientTerms?.map((p) => ({
                patientTerm: p.patientTerm,
                text: p.text,
              })) || []
            ) || [];

          secDescription.patientTerms = sectionDiscripPatientTermsText;
          secDescription.languageCode = sectionDiscripPatientTermsText.length > 0 ? res.sectionDescription![0].languageCode : "";
          secDescription.sectionDescriptionText = sec.sectionDescriptionText;

          const secQuestHeaderPatientTermsText = res.sectionQuestionHeader?.filter((sqh) =>
            sqh.languageCode?.toLocaleLowerCase() === culture?.toLowerCase()).flatMap((sqh) =>
              sqh.patientTerms?.map((p) => ({
                patientTerm: p.patientTerm,
                text: p.text,
              })) || []
            ) || [];

          secQuestionHeader.patientTerms = secQuestHeaderPatientTermsText;
          secQuestionHeader.languageCode = secQuestHeaderPatientTermsText.length > 0 ? res.sectionQuestionHeader![0].languageCode : "";
          secQuestionHeader.sectionQuestionHeaderText = sec.sectionQuestionHeaderText;


          const secTitlePatientTermsText = res.sectionTitle?.filter((st) =>
            st.languageCode?.toLocaleLowerCase() === culture?.toLocaleLowerCase()).flatMap((st) =>
              st.patientTerms?.map((p) => ({
                patientTerm: p.patientTerm,
                text: p.text,
              })) || []
            ) || [];

          secSectionTitle.patientTerms = secTitlePatientTermsText;
          secSectionTitle.languageCode = secTitlePatientTermsText.length > 0 ? res.sectionTitle![0].languageCode : "";
          secSectionTitle.sectionTitleText = sec.sectionQuestionHeaderText;

          sections.push({
            sectionName: sec.sectionName,
            sectionDescriptionText: sec.sectionDescriptionText,
            sectionQuestionHeaderText: sec.sectionQuestionHeaderText,
            sectionTitleText: sec.sectionTitleText,
            sectionDescription: secDescription,
            sectionQuestionHeader: secQuestionHeader,
            sectionTitle: secSectionTitle
          });
        });

        // Modify the Intrument data to keep only the selected culture
        const instrument: Instrument = {
          instrumentsId: res.instrumentsId,
          instrumentsType: res.instrumentsType,
          description: res.description,
          instrumentName: res.instrumentName?.filter((inLang) => { return inLang.languageCode?.toLowerCase() == culture?.toLowerCase() }).map((x) => { return x.instrumentNameText }).toString(),
          introductionText: res.introduction?.filter((introLang) => { return introLang.languageCode?.toLowerCase() == culture?.toLowerCase() }).map((x) => { return x.introText }).toString(),
          helpText: res.help?.filter((hLang) => { return hLang.languageCode?.toLowerCase() == culture?.toLowerCase() }).map((x) => { return x.helpText }).toString(),
          termsConditionsText: res.termsConditions?.filter((tcLang) => { return tcLang.languageCode?.toLowerCase() == culture?.toLowerCase() }).map((x) => { return x.termsConditionText }).toString(),
          section: sections,
          availableLanguages: res.availableLanguages,
          help: new HelpAPI,
          introduction: new IntroductionAPI,
          termsConditions: new TermsConditionsAPI
        }

        const help = new HelpAPI;
        const introduction = new IntroductionAPI;
        const termsConditions = new TermsConditionsAPI;

        help.helpText = instrument.helpText;

        const helpPatientTermsText = res.help?.filter((st) =>
          st.languageCode?.toLocaleLowerCase() === culture?.toLocaleLowerCase()).flatMap((st) =>
            st.patientTerms?.map((p) => ({
              patientTerm: p.patientTerm,
              text: p.text,
            })) || []
          ) || [];

        help.patientTerms = helpPatientTermsText;
        help.languageCode = helpPatientTermsText.length > 0 ? res.help![0].languageCode : "";
        instrument.help = help;

        const introPatientTermsText = res.introduction?.filter((ipt) =>
          ipt.languageCode?.toLocaleLowerCase() === culture?.toLocaleLowerCase()).flatMap((ipt) =>
            ipt.patientTerms?.map((p) => ({
              patientTerm: p.patientTerm,
              text: p.text,
            })) || []
          ) || [];

        introduction.patientTerms = introPatientTermsText;
        introduction.languageCode = introPatientTermsText.length > 0 ? res.introduction![0].languageCode : "";
        instrument.introduction = introduction;

        const termsConditionPatientTermsText = res.termsConditions?.filter((tc) =>
          tc.languageCode?.toLocaleLowerCase() === culture?.toLocaleLowerCase()).flatMap((tc) =>
            tc.patientTerms?.map((p) => ({
              patientTerm: p.patientTerm,
              text: p.text,
            })) || []
          ) || [];

        termsConditions.patientTerms = termsConditionPatientTermsText;
        termsConditions.languageCode = termsConditionPatientTermsText.length > 0 ? res.termsConditions![0].languageCode : "";
        instrument.termsConditions = termsConditions;

        return instrument;

      })
      );
  }

  /**
   * 
   * @param launchId 
   * @param isPreview 
   * @returns 
   */
  getQuestionnaire(launchId: string, isPreview: boolean = false): Observable<Questionnaire> {
    return this.httpClient.get<Questionnaire>(`${API_URL}/final`, {
      params: {
        "launchId": launchId,
        "isPreview": isPreview
      }
    })
  }
}
